<script>
export default {
  name: "DidoDownloadButtonDataset",
};
</script>

<script setup>
import { ref, watch, computed } from "vue";
import JSZip from "jszip";
import api from "../../../api";
import { downloadBlob } from "../../../utils";
import DidoDownloadItemAttachment from "../items/Attachment.vue";
import DidoDownloadItemMillesime from "../items/Millesime.vue";

// define Props
const props = defineProps({
  apiUrl: { type: String, required: true },
  dataset: { type: Object, required: true },
});

// define Reactive Data
const selectedLinks = ref([]);
const zipInProgress = ref(false);

// define computed datas
const buttonTooltip = computed(() => {
  if (selectedLinks.value.length)
    return `Télécharger une archive ZIP de votre sélection du jeu de données '${props.dataset.title}'`;
  return `Télécharger une archive ZIP (csv des fichiers de données et pièces jointes) du jeu de données '${props.dataset.title}'`;
});
const buttonLabel = computed(() => {
  if (zipInProgress.value) return "en préparation";
  if (selectedLinks.value.length) return "vos fichiers sélectionnés";
  return "csv des fichiers de données et pièces jointes";
});
const selectedLinksTooltip = computed(() => selectedLinks.value.join("\n"));
const lastDatafilesMillesimesOfDataset = computed(() => {
  if (!props.dataset) return [];
  return props.dataset.datafiles
    .map((datafile) => {
      const lastMillesime = datafile.millesimes
        .filter(
          ({ date_diffusion: dateDiffusion }) =>
            new Date() > new Date(dateDiffusion),
        )
        .map(({ millesime, geoFields }) => ({ millesime, geoFields }))
        .sort((m1, m2) => new Date(m1.millesime) - new Date(m2.millesime))
        .reverse()[0];
      return {
        rid: datafile.rid,
        title: datafile.title,
        description: datafile.description,
        millesime: lastMillesime ? lastMillesime.millesime : null,
        geoFields: lastMillesime ? lastMillesime.geoFields || [] : null,
        baseLink: api.urlDatafileBase(props.apiUrl, datafile.rid),
      };
    })
    .filter(({ millesime }) => millesime);
});
const defaultLinks = computed(() => {
  return [
    ...props.dataset.attachments
      .filter(({ type }) => !type || type === "documentation")
      .map(({ url }) => url),
    ...lastDatafilesMillesimesOfDataset.value.map(
      ({ baseLink, millesime }) =>
        `${baseLink}/csv?millesime=${millesime}&withColumnName=true&withColumnDescription=true&withColumnUnit=false`,
    ),
  ];
});

// define watchers
watch(
  () => props.dataset,
  () => resetButton(),
);

// define methods
const resetButton = () => {
  selectedLinks.value = [];
  zipInProgress.value = false;
};
const resetSelection = () => {
  selectedLinks.value = [];
};
const updateSelectedLinks = ({ action, link }) => {
  if (action === "add") {
    if (!selectedLinks.value.find((selected) => selected === link))
      selectedLinks.value = [...selectedLinks.value, link];
  }
  if (action === "remove") {
    selectedLinks.value = selectedLinks.value.filter(
      (selected) => selected !== link,
    );
  }
};
const downloadZip = async () => {
  zipInProgress.value = true;
  const linksInArchive = selectedLinks.value.length
    ? selectedLinks.value
    : defaultLinks.value;
  const zip = new JSZip();
  const promises = linksInArchive.map(
    (link) =>
      new Promise((resolve, reject) =>
        api
          .getNameAndDataFromLink(link)
          .catch(() => reject(`Error during downloading ${link}`))
          .then(({ name, data }) => zip.file(name, data))
          .catch(() => reject(`Error during adding ${link} to zip`))
          .then(() => resolve(true)),
      ),
  );
  const downloadResult = await Promise.all(promises).catch(
    (e) => !!console.log(e),
  );

  if (!downloadResult) {
    zipInProgress.value = false;
    return;
  }

  await zip
    .generateAsync({
      type: "blob",
      compression: "DEFLATE",
      compressionOptions: { level: 9 },
      comment: "Archive created by DiDo Widget",
    })
    .then((blob) => downloadBlob(blob, "dido.zip"))
    .catch((e) => console.log("Error during zip generation", e))
    .finally(() => (zipInProgress.value = false));
};
</script>

<template>
  <QBtnDropdown
    split
    color="secondary"
    no-caps
    dense
    dropdown-icon="mdi-puzzle-edit"
    no-icon-animation
    size="md"
    unelevated
    :title="buttonTooltip"
    :disable="zipInProgress"
    :disable-dropdown="zipInProgress"
    :content-class="['no-border-radius', 'no-box-shadow', 'border-secondary']"
    class="dido-download-dataset no-border-radius"
    @click="downloadZip"
  >
    <template #label>
      <div class="fit row-start-center no-wrap">
        <QSpinner v-if="zipInProgress" color="white" size="24px" />
        <QIcon v-else name="mdi-archive" />
        <div class="q-ml-sm full-heigh column-all-start">
          <div
            class="dido-download-dataset-button-label-text-main row-start-center no-wrap"
          >
            <span class="q-mr-sm">Archive ZIP</span>
            <QBadge
              v-if="selectedLinks.length"
              :label="selectedLinks.length"
              outline
              :title="selectedLinksTooltip"
            />
          </div>
          <div
            class="dido-download-dataset-button-label-text-sub text-caption text-italic text-left"
          >
            {{ buttonLabel }}
          </div>
        </div>
      </div>
    </template>
    <div class="q-pa-sm text-italic">
      Faites votre sélection en cochant les cases ou accéder directement au
      téléchargement d'un fichier<br /><span
        class="text-secondary text-italic cursor-pointer"
        title="Réinitialiser la sélection"
        @click="resetSelection"
        >ràz sélection</span
      >
    </div>
    <QList separator>
      <QSeparator />
      <DidoDownloadItemMillesime
        v-for="(millesimeObject, index) in lastDatafilesMillesimesOfDataset"
        :key="`millesime-${index}`"
        :api-url="props.apiUrl"
        :millesime-object="millesimeObject"
        selectable
        :selected-links="selectedLinks"
        @update-selected-links="updateSelectedLinks"
      />
      <DidoDownloadItemAttachment
        v-for="(attachment, index) in props.dataset.attachments"
        :key="`attachment-${index}`"
        :attachment="attachment"
        selectable
        :selected-links="selectedLinks"
        @update-selected-links="updateSelectedLinks"
      />
    </QList>
  </QBtnDropdown>
</template>

<style lang="sass" scoped>
.dido-download-dataset
  min-height: 60px
  max-height: 60px
  .dido-download-dataset-button-label-text-main
    font-size: 0.75rem
  .dido-download-dataset-button-label-text-sub
    font-size: 0.6rem
    line-height: 0.8rem
    white-space: normal
    min-width: 128px
    max-width: 128px
  :deep( button.q-btn)
    :hover
      filter: opacity(0.65)
</style>
